import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Block } from 'models/response/model.interface';
import HomeIcon from 'components/Icons/Home';
import { getBaseScenario } from 'redux/ScenarioSlice';
import apiService from 'services';
import { GetUserModels } from 'redux/ModelsSlice';
import { getPlannerMode, handlePlannerMode } from 'redux/PlannerModeSlice';

import Switch from './Switch';

interface NavigationMenuData {
  blocks: Block[];
  id: number;
  name: string;
}

const NavigationHeader = () => {
  const navigate = useNavigate();
  const { modelId, blockId } = useParams();
  const { pathname } = useLocation();
  const baseScenario = useSelector(getBaseScenario);
  const [mobileVersion] = useMediaQuery('(max-width: 1024px)');
  const dispatch: any = useDispatch();
  const plannerMode = useSelector(getPlannerMode);
  const models = useSelector((state: any) => state.modelsSlice.models);

  useEffect(() => {
    dispatch(GetUserModels({}));
  }, []);

  const currentModel = useMemo(() => {
    return models?.find((element: NavigationMenuData) => modelId === String(element.id));
  }, [models, modelId]);

  const currentBlock = useMemo(() => {
    return currentModel?.blocks?.find((element: Block) => blockId === String(element.id));
  }, [currentModel?.blocks, blockId]);

  const otherModels = useMemo(() => {
    return models.filter((element: any) => element.id !== currentModel?.id);
  }, [currentModel?.id, models]);

  const routeToOverviewPage = (model_id: string, block_id?: string) => {
    if (!block_id) {
      navigate(`/model/${model_id}/home`);
    } else {
      const lastUrlIndicator = pathname.split('/').pop();
      if (lastUrlIndicator?.toLocaleLowerCase() === 'drivers') {
        apiService
          .get_block_drivers(Number(blockId), {
            params: {
              scenario_id: Number(baseScenario?.id),
            },
          })
          .then((driversData: any) => {
            if (driversData.data.drivers.length > 0) {
              navigate(`/model/${model_id}/block/${block_id}/${lastUrlIndicator}`);
            } else navigate(`/model/${model_id}/block/${block_id}/plan`);
          });
      } else navigate(`/model/${model_id}/block/${block_id}/${lastUrlIndicator}`);
    }
  };

  const handleModelChange = (model_Id: number) => {
    const model = models?.find((element: NavigationMenuData) => model_Id === element.id);
    if (model?.blocks && model?.blocks.length > 0) {
      routeToOverviewPage(String(model?.id), String(model.blocks[0].id));
    } else {
      routeToOverviewPage(String(model?.id));
    }
  };

  const navigateToHome = () => {
    navigate(`/home`);
  };

  return (
    <Flex
      borderBottom={'1px solid white'}
      h={mobileVersion ? '40px' : '34px'}
      bg='#36326A'
      justifyContent={'space-between'}
      textAlign={'center'}
      px={4}
    >
      {modelId && (
        <Flex alignItems={'center'}>
          <Flex alignItems={'center'} justifyContent='space-between'>
            <Text
              textAlign='center'
              paddingBottom={'5px'}
              cursor='pointer'
              onClick={() => navigateToHome()}
            >
              <HomeIcon />
            </Text>
            {!mobileVersion && (
              <Text
                _hover={{ textDecoration: 'underline' }}
                cursor='pointer'
                fontFamily='regular_roboto'
                color='white'
                fontSize={mobileVersion ? 'md' : 'xs'}
                textAlign='center'
                pl={3}
                onClick={() => navigateToHome()}
              >
                My models
              </Text>
            )}
          </Flex>
          <Text mx={2}>/</Text>
          <Text
            _hover={{ textDecoration: 'underline' }}
            onClick={() => routeToOverviewPage(modelId)}
            maxWidth={mobileVersion ? '40vw' : '35vw'}
            fontFamily='regular_roboto'
            overflow='hidden'
            whiteSpace={'nowrap'}
            textOverflow='ellipsis'
            fontSize={'xs'}
            cursor='pointer'
          >
            {currentModel?.name}
          </Text>
          <Menu>
            <MenuButton
              border='none'
              textTransform={'none'}
              fontWeight='normal'
              color='white'
              textAlign='center'
              pl={0}
              boxShadow='none'
              width={'auto'}
              ml={2}
            >
              <ChevronDownIcon height={'16px'} width={'16px'} />
            </MenuButton>
            <MenuList borderRadius={'10px'}>
              <Box
                maxHeight={'50vh'}
                overflowY='scroll'
                className='scroll'
                zIndex={'dropdown'}
                scrollBehavior={'smooth'}
              >
                <MenuOptionGroup type='radio' value={modelId}>
                  {otherModels?.map((model: any) => {
                    return (
                      <MenuItemOption
                        key={model.id}
                        minH='30px'
                        color='black'
                        value={model?.id}
                        onClick={() => handleModelChange(model?.id)}
                        fontSize={mobileVersion ? 'sm' : 'xs'}
                        textAlign='center'
                      >
                        <Text
                          textOverflow={'ellipsis'}
                          width={'200px'}
                          whiteSpace={'nowrap'}
                          overflow={'hidden'}
                          fontFamily='regular_roboto'
                          textAlign={'start'}
                        >
                          <abbr style={{ textDecoration: 'none' }} title={model?.name}>
                            {model?.name}
                          </abbr>
                        </Text>
                      </MenuItemOption>
                    );
                  })}
                </MenuOptionGroup>
              </Box>
            </MenuList>
          </Menu>
          <Text mx={2}>/</Text>
          {currentModel?.blocks && currentModel?.blocks?.length > 1 && (
            <Menu>
              <MenuButton
                width={'auto'}
                border='none'
                textTransform={'none'}
                fontWeight='normal'
                as={Button}
                rightIcon={<ChevronDownIcon height={'16px'} width={'16px'} />}
                color='white'
                textAlign='center'
                p={0}
                boxShadow='none'
                maxWidth={mobileVersion ? '40vw' : '35vw'}
                _hover={{ textDecoration: 'underline' }}
              >
                <Text
                  fontFamily='regular_roboto'
                  overflow='hidden'
                  whiteSpace={'nowrap'}
                  textOverflow='ellipsis'
                  fontSize={'xs'}
                >
                  {currentBlock?.name}
                </Text>
              </MenuButton>
              <MenuList borderRadius={'10px'}>
                <Box
                  maxHeight={'50vh'}
                  overflowY='scroll'
                  className='scroll'
                  zIndex={'dropdown'}
                  scrollBehavior={'smooth'}
                >
                  <MenuOptionGroup type='radio' value={blockId}>
                    {currentModel?.blocks?.map((block: any) => {
                      return block.id !== currentBlock?.id ? (
                        <MenuItemOption
                          key={block.id}
                          minH='30px'
                          color='black'
                          value={block?.id}
                          onClick={() => routeToOverviewPage(String(modelId), String(block?.id))}
                          fontSize={mobileVersion ? 'sm' : 'xs'}
                          textAlign='center'
                        >
                          <Text fontFamily='regular_roboto' textAlign={'start'}>
                            {block?.name}
                          </Text>
                        </MenuItemOption>
                      ) : null;
                    })}
                  </MenuOptionGroup>
                </Box>
              </MenuList>
            </Menu>
          )}
          {currentModel?.blocks?.length === 1 && (
            <Text fontSize={'xs'} fontFamily='regular_roboto'>
              {currentBlock?.name}
            </Text>
          )}
        </Flex>
      )}
      {!mobileVersion && (
        <Flex alignItems={'center'}>
          <Text fontSize={'xs'} mr={3}>
            Builder mode
          </Text>
          <Switch
            isSelected={plannerMode}
            handleOnChange={(event: any) => dispatch(handlePlannerMode(event.target.checked))}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default NavigationHeader;
